import React from "react";
import "./legal.scss";

function TermsOfUse() {
  return (
    <div className="legalPage">
      <h1>Terms of Use of the Speqqle SaaS Platform</h1>
      <p><em>Last updated: 07 October 2025</em></p>
      <p><em>Effective as of 09 October 2025 (replacing prior beta-use terms)</em></p>

      <div className="section">
        <h2>1. Introduction</h2>
        <p>
          Welcome to the Speqqle SaaS platform. By accessing or using the
          platform, whether under a trial or paid subscription plan,
          you agree to be bound by these Terms of Use. These terms outline your
          rights and obligations as a user, as well as the rights and obligations
          of Speqqle SA.
        </p>
        <p>
          If you do not agree to these terms, please do not access or use the
          Speqqle platform.
        </p>
      </div>

      <div className="section">
        <h2>2. Definitions</h2>
        <ul>
          <li>
            <strong>&quot;Speqqle&quot;</strong> refers to Speqqle SA, the
            provider of the SaaS platform.
          </li>
          <li>
            <strong>&quot;Platform&quot;</strong> refers to the Speqqle
            software-as-a-service (SaaS) platform, including all applications,
            features, and content made available under any trial or paid subscription plan.
          </li>
          <li>
      <strong>&quot;User&quot;</strong>: The individual or entity who accesses or uses
      the platform, including employees, consultants, or other representatives
      acting on their behalf.
    </li>
    <li>
      <strong>&quot;Account&quot;</strong>: The registered access credentials created
      by or for a User to enable use of the platform.
    </li>
          <li>
            <strong>&quot;Subscription Plan&quot;</strong> refers to the plan
            you select (e.g., Trial, Basic, or other paid tiers), as described on the
            pricing page or in an order form.
          </li>
          <li>
      <strong>&quot;Subscription Term&quot;</strong>: The recurring billing period
      (monthly, annual, or otherwise) for which a subscription plan is active.
    </li>
          <li>
            <strong>&quot;User Data&quot;</strong> means spectral data, metadata, models, or
            results uploaded or generated by you while using the platform.
          </li>
          <li>
            <strong>&quot;Personal Data&quot;</strong> refers to personal data
            as defined under GDPR and Swiss data protection laws.
          </li>
          <li>
            <strong>&quot;Feedback&quot;</strong> refers to any ideas,
            suggestions, reports, or data you submit about the platform.
          </li>
          <li>
      <strong>&quot;Integration&quot;</strong>: A connection between the platform and
      external systems, such as electronic lab notebooks (ELNs), laboratory
      information management systems (LIMS), or cloud storage services.
    </li>
    <li>
      <strong>&quot;Aggregated Data&quot;</strong>: Data combined with other user data
      in a way that no individual user or organization can reasonably be
      identified.
    </li>
    <li>
      <strong>&quot;Anonymized Data&quot;</strong>: Data that has been processed to
      remove personal identifiers such that the individual cannot reasonably
      be re-identified.
    </li>
    <li>
      <strong>&quot;Benchmarking Data&quot;</strong>: Aggregated and anonymized
      information derived from usage of the platform, used to generate
      statistical analyses, performance metrics, or industry insights.
    </li>
    <li>
      <strong>&quot;Third-Party Service Provider&quot;</strong>: An external provider
      engaged by Speqqle to deliver services such as payment processing,
      hosting, storage, or customer support (e.g., Stripe for billing).
    </li>
    <li>
      <strong>&quot;Grace Period&quot;</strong>: The period following termination of
      a subscription during which you may export your User Data stored as part of a data-storage plan or saved projects before it is deleted per our retention schedules. This grace period does not apply to system logs, caches, or backups retained for security, reliability, billing, or legal compliance.
    </li>
        </ul>
      </div>

      <div className="section">
        <h2>3. Subscription Plans and Access Rights</h2>
        <ul>
          <li>
            <strong>License Grant</strong>: Speqqle SA grants you a
            non-exclusive, non-transferable license to access and use the
            platform according to your selected subscription plan.
          </li>
          <li>
            <strong>Plan Features</strong>: The features, limits, and fees of
            each plan are described on our pricing page or in the applicable
            order form. Speqqle may introduce new plans or modify existing ones
            from time to time, with notice to users.
          </li>
          <li>
            <strong>Commercial Use</strong>: Paid plans may be used for
            commercial, research, or production purposes. Trial plans are intended for evaluation, testing, and non-commercial use unless otherwise
            specified.
          </li>
          <li>
            <strong>Platform IP (no transfer)</strong>: Speqqle retains all rights,
            title, and interest in and to the platform, including intellectual
            property rights. Nothing in these Terms transfers any Speqqle IP to you.
          </li>
          <li>
            <strong>No Distribution</strong>: You may not distribute, sublicense,
            or share access to the platform with unauthorized third parties.
          </li>
          <li>
            <strong>Trial Period</strong>: Speqqle may offer limited free trials to new users for evaluation purposes. Trial periods automatically expire after the stated duration (typically 14 days). Continued access to the platform thereafter requires activation of a paid plan.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>4. Fees, Billing, and Upgrades/Downgrades</h2>
        <ul>
          <li>
            <strong>Fees</strong>: Paid plans are subject to fees as presented 
            at checkout or in the order form.
          </li>
          <li>
            <strong>Billing</strong>: All payments are processed securely by our
            third-party payment provider, Stripe, which complies with PCI-DSS
            standards. Speqqle does not collect or store your complete payment card
            details. By providing payment information, you agree to Stripe&apos;s
            applicable terms and privacy policy in addition to these Terms of Use.
          </li>
          <li>
            <strong>Subscriptions</strong>: Subscriptions are billed in advance on a 
            recurring basis (monthly or annually, as specified). Fees are non-refundable
            except where required by law.
          </li>
           <li>
            <strong>Renewal and Reminders</strong>: Annual subscriptions automatically renew unless cancelled prior to the renewal date. Users will receive a reminder by email before renewal charges are processed. You may cancel at any time; cancellations take effect at the end of the current billing cycle.
          </li>
          <li>
            <strong>Upgrades</strong>: If you upgrade your subscription, the upgrade takes effect immediately and a new billing period begins on the upgrade date. The applicable fee is charged at that time. We apply a pro-rata credit for any unused time from your prior plan.
          </li>
          <li>
            <strong>Downgrades</strong>: Downgrades apply at the start of the next 
            billing cycle.
          </li>
          <li>
            <strong>Changes</strong>: Speqqle may adjust pricing with prior notice.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>5. User Obligations</h2>
        <ul>
          <li>
            <strong>Responsible Use</strong>: You agree not to use the platform
            for unlawful, harmful, or abusive purposes.
          </li>
          <li>
            <strong>Feedback</strong>: You may provide suggestions, bug reports,
            or usability feedback. Your feedback is appreciated but voluntary
            unless otherwise agreed.
          </li>
          <li>
            <strong>Compliance</strong>: You must comply with all applicable
            laws and these Terms when using the platform.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>6. Prohibited Activities</h2>
        <p>You may not:</p>
        <ul>
          <li>Reverse engineer, decompile, or attempt to derive source code.</li>
          <li>Modify, copy, or create derivative works of the platform.</li>
          <li>Scrape or crawl APIs beyond intended usage.</li>
          <li>Upload malicious code or scripts.</li>
          <li>Exploit the platform in ways not permitted under your plan.</li>
          <li>Engage in activity that harms the platform&apos;s integrity, performance, or security.</li>
        </ul>
      </div>

      <div className="section">
        <h2>7. Data Ownership and Use</h2>
        <p>
          <strong>Ownership:</strong> You retain all rights, title, and interest in
          and to your User Data. Speqqle does not claim ownership of the spectral
          data or results you upload or generate on the platform.
        </p>
        <p>
          <strong>(a) User-linked data:</strong> Data that can be connected to your
          account remains confidential and is used solely to provide you with
          services, such as enabling your analyses, storing your work, or assisting
          you if you request support or consulting. This data will not be used by
          Speqqle for internal development or external purposes without your
          explicit consent.
        </p>
        <p>
          <strong>(b) Anonymous data:</strong> Speqqle may collect and use
          aggregated, de-identified, or anonymized data that cannot reasonably be
          used to identify you. Such data may be used for debugging, analytics,
          improving algorithms, training artificial intelligence, and developing new
          features.
        </p>
        <p>
          <strong>(c) Data retention and technical traces:</strong> Speqqle does not intentionally retain or access non-anonymous uploaded User Data beyond what is necessary to provide the service. During normal operation, certain temporary copies or fragments of User Data may remain on our servers (e.g., within session caches, error logs, or automated backups) for a limited period due to technical processes or unexpected interruptions. Such residual data is never accessed, analyzed, or shared, and is automatically overwritten or securely deleted as part of routine system maintenance.

If you explicitly subscribe to a plan that includes data storage or project saving, your User Data will be retained and remains accessible only to you and authorized Speqqle personnel for support purposes.
        </p>
        <p>
          <strong>(d) Voluntarily shared data:</strong> You may choose to provide
          Spectral Data to Speqqle for broader use, such as research, product
          validation, or publication. Such sharing is strictly opt-in, subject to a
          separate agreement or consent process, and may include compensation or
          acknowledgment as agreed with you in advance.
        </p>
      </div>

      <div className="section">
        <h2>8. Data Protection and Privacy</h2>
        <p>
          Speqqle SA respects your privacy and processes personal data in
          accordance with GDPR and Swiss data protection laws. The collection,
          use, and processing of data are governed by the{" "}
          <a
            href={`${window.location.origin}/privacy-policy`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Speqqle Privacy Statement
          </a>
          . In the event of any inconsistency between this section and the Privacy Statement, the Privacy Statement shall prevail for data processing matters.
        </p>
        <p>
          Personal data may include identity data, contact data, feedback, and
          technical data. Data is processed to enable access to the platform,
          deliver services, improve products, and communicate with you.
        </p>
      </div>

      <div className="section">
        <h2>8a. Data Deletion Requests (Right to Erasure)</h2>
        <p>
          In accordance with the GDPR and Swiss data protection laws, you have the
          right to request the deletion of your personal data at any time. Requests
          can be made by contacting Speqqle at{" "}
          <a href="mailto:contact@speqqle.com">contact@speqqle.com</a>.
        </p>
        <p>
          Upon receipt of a valid request, Speqqle will process and complete the
          deletion within 30 days, unless retention of certain data is legally
          required (e.g., for accounting or regulatory purposes). In such cases,
          Speqqle will inform you of the legal basis for retention.
        </p>
      </div>

      <div className="section">
        <h2>8b. Communications</h2>
        <p>
          By creating an account, you agree that Speqqle may contact you by email or
          through in-platform notifications for purposes directly related to the
          service. These communications include, but are not limited to, account
          management, billing information, security alerts, feature changes and
          updates to functionality available under your current plan, and changes to
          these Terms of Use. Such messages are considered essential for the use of
          the platform and cannot be opted out of.
        </p>
        <p>
          Messages that primarily promote new products, higher-tier subscription
          plans, or additional paid features are considered marketing communications.
          These will only be sent if you have given your prior consent, and you may
          withdraw your consent or opt out at any time by following the unsubscribe
          instructions provided in such messages or by contacting{" "}
          <a href="mailto:contact@speqqle.com">contact@speqqle.com</a>.
        </p>
        <p>
          Speqqle may also present information about new features or offers within
          the platform interface (e.g., banners, tooltips, or pop-up messages). These
          in-platform notices are part of the normal use of the service.
        </p>
      </div>

      <div className="section">
        <h2>9. Analytics, Logs, and Benchmarking</h2>
        <p>
          To improve and grow the platform, Speqqle may collect and process certain
          additional categories of information. Such processing is always carried out
          in compliance with GDPR and Swiss data protection law, and, where possible,
          in aggregated or anonymized form.
        </p>
        <ul>
          <li>
            <strong>Usage analytics:</strong> We may collect data about how you
            interact with the platform (e.g., features used, time spent, navigation
            flows). This helps us understand usage patterns, improve usability, and
            prioritize development.
          </li>
          <li>
            <strong>Performance and diagnostics:</strong> We may collect technical
            data such as error logs, browser or device information, and performance
            statistics. This is used for debugging, monitoring reliability, and
            ensuring system stability.
          </li>
          <li>
            <strong>Support communications:</strong> When you contact us (e.g., by
            email or in-app forms), we retain the content of your communication to
            resolve your issue, train our support team, and improve future responses.
          </li>
          <li>
            <strong>Third-party integrations:</strong> If you connect Speqqle to
            external systems (e.g., ELNs, LIMS, or cloud drives), we may process
            integration credentials and data flows as needed to enable that
            connection. Such data will only be used for the purpose of providing the
            integration.
          </li>
          <li>
            <strong>Anonymized benchmarking:</strong> We may aggregate and anonymize
            user data to produce statistical analyses, benchmarks, and industry
            insights. These outputs will never identify individual users or
            organizations but may be used in reports, marketing, or collaborations.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>10. Feedback and Intellectual Property</h2>
        <p>
          Any feedback you provide (e.g., bug reports, suggestions, or
          improvements) becomes the property of Speqqle SA. By submitting
          feedback, you assign to Speqqle SA all rights, title, and interest in
          that feedback without compensation.
        </p>
      </div>

      <div className="section">
        <h2>11. Platform Availability and Updates</h2>
        <ul>
          <li>
            <strong>No Guarantees</strong>: The platform is provided on an
            &quot;as is&quot; and &quot;as available&quot; basis. Speqqle SA
            does not guarantee uninterrupted or error-free operation.
          </li>
          <li>
            <strong>Maintenance and Updates</strong>: Speqqle SA may update,
            modify, or discontinue features of the platform with reasonable
            notice to users.
          </li>
          <li>
            <strong>No Liability for Downtime</strong>: Speqqle SA is not
            responsible for downtime, delays, or interruptions.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>12. Warranty Disclaimer</h2>
        <p>
          The platform is provided without warranties of any kind, express or
          implied, including fitness for a particular purpose, non-infringement,
          or accuracy of results.
        </p>
      </div>

      <div className="section">
        <h2>13. Limitation of Liability and Indemnification</h2>
        <p>
          To the maximum extent permitted by law, Speqqle SA will not be liable
          for indirect, incidental, consequential, or punitive damages,
          including lost data or revenue. You agree to indemnify and hold
          Speqqle harmless from claims arising from your misuse of the platform
          or violation of these Terms.
        </p>
      </div>

      <div className="section">
        <h2>14. Termination of Use</h2>
        <ul>
          <li>
            <strong>By Speqqle</strong>: We may suspend or terminate your access
            if you breach these Terms or fail to pay applicable fees.
          </li>
          <li>
            <strong>By User</strong>: You may terminate your subscription at any
            time. Fees are non-refundable except as required by law.
          </li>
          <li>
            <strong>Post-Termination</strong>: Upon termination, your right to
            access the platform ends. You may export your data from the platform within a grace period (30 days) for any plan that includes stored projects/data. System logs, caches, or backups may be retained for limited periods for security, reliability, billing, or legal compliance.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>15. Modification of Terms</h2>
        <p>
          Speqqle SA may update these Terms of Use from time to time. Users will
          be notified of material changes by email or in-platform notification.
          Continued use after changes take effect constitutes acceptance.
        </p>
      </div>

      <div className="section">
        <h2>16. Export Compliance</h2>
        <p>
          You agree to comply with all applicable export control and
          international trade laws. You may not access or use the platform in
          violation of such laws or permit access by others in restricted
          countries.
        </p>
      </div>

      <div className="section">
        <h2>17. Governing Law and Dispute Resolution</h2>
        <p>
          These Terms of Use are governed by the laws of{" "}
          <strong>Switzerland</strong>. Any disputes arising from these terms
          will be subject to the exclusive jurisdiction of the courts of{" "}
          <strong>Geneva, Switzerland</strong>.
        </p>
      </div>

      <div className="section">
        <h2>18. Miscellaneous</h2>
        <p>
          These Terms constitute the entire agreement between you and Speqqle.
          If any provision is found invalid, the remaining provisions remain in
          force. Our failure to enforce a right does not waive it. You may not
          assign these Terms without our consent; Speqqle may assign them in
          connection with a merger or acquisition. Neither party is liable for
          delays due to causes beyond reasonable control (force majeure).
        </p>
      </div>

      <div className="section">
        <h2>19. Contact Us</h2>
        <p>
          If you have questions, comments, or concerns about these Terms of Use,
          please contact us at{" "}
          <a href="mailto:contact@speqqle.com">contact@speqqle.com</a>.
        </p>
      </div>
    </div>
  );
}

export default TermsOfUse;
